import type {CreditsPillContent} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {CreditsPillTheme} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import type {ReactElement} from 'react';
import Tooltip from 'react-bootstrap/Tooltip';
import styles from './credits-pill.module.scss';
import {Text, TextSize} from '@Components/text';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {formatNumber, formatToThousand} from '@Utils/number.util';
import type {OverlayInjectedProps} from 'react-bootstrap/Overlay';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {IconSize} from '@Components/icon-v2/icon.types';

interface CreditsPillProps {
  showPill: boolean;
  theme: CreditsPillTheme;
  content: CreditsPillContent;
}

export function CreditsPill({showPill, theme, content}: CreditsPillProps): ReactElement {
  const {actionCost, remainingCredits, isMaxEstimate = false} = content;

  const getCostText = (): string => {
    if (actionCost <= 1) {
      return isMaxEstimate ? window.i18next.t('pmwjs_will_cost_up_to_1_credit') : window.i18next.t('pmwjs_will_cost_1_credit');
    }
    const key = isMaxEstimate ? 'pmwjs_will_cost_up_to_x_credits' : 'pmwjs_will_cost_x_credit';
    return window.i18next.t(key, {creditsCost: actionCost});
  };

  const getTooltipContent = (): ReactElement => {
    return (
      <div className="flex-center flex-v-row">
        <Text dangerouslySetInnerHTML size={TextSize.SM} val={getCostText()} />
        <Text size={TextSize.SM} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: formatNumber(remainingCredits)})} />
      </div>
    );
  };

  const getCreditsPillTooltip = (): ReactElement<OverlayInjectedProps, string> => {
    return <Tooltip className={`spacing-m-b-2`}>{getTooltipContent()}</Tooltip>;
  };

  const getPill = (): ReactElement => {
    return (
      <div>
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.HYBRID,
            icon: 'icon-flash',
            text: formatToThousand(actionCost),
            isBold: true,
            textColor: theme === CreditsPillTheme.WARNING ? 'content-premium' : 'content-secondary',
            textSize: TextSize.SM,
            iconSize: IconSize.SIZE_ICON_12,
          }}
          background={PILL_BACKGROUND_COLOR.TRANSPARENT}
          size={PILL_SIZE.XSMALL}
          className={`spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1 ${styles.creditsPillContainer} ${theme === CreditsPillTheme.WARNING ? styles.pillHoverPremium : ''}`}
        />
      </div>
    );
  };

  const getCreditsPill = (): ReactElement => {
    return (
      <OverlayTrigger trigger={['hover', 'focus', 'click']} overlay={getCreditsPillTooltip()} delay={{show: 200, hide: 200}} placement="top" show={showPill ? undefined : false}>
        {getPill()}
      </OverlayTrigger>
    );
  };

  return getCreditsPill();
}
